import React, { useMemo } from 'react';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import { Alert, Avatar, Box, Chip, Typography } from '@mui/material';
import ConfirmationModal from '../../ui/primitives/ConfirmationModal';
import CustomSelect from '../../ui/primitives/CustomSelect';
import Loader from '../../components/elements/Loader';

const UserCard = ({ initials, name, email, projects }) => (
  <Box sx={{
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    px: 1.5,
    py: 1,
    borderRadius: 1,
    '&:not(:last-child)': { borderBottom: '1px solid', borderColor: 'divider' },
  }}>
    <Avatar sx={{ width: 32, height: 32, fontSize: '0.65rem', bgcolor: 'primary.main', flexShrink: 0 }}>
      {initials}
    </Avatar>
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '13px', lineHeight: 1.3 }} noWrap>
        {name}
      </Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '11px' }} noWrap>
        {email}
      </Typography>
    </Box>
    {projects?.length > 0 && (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'flex-end', maxWidth: 200 }}>
        {projects.map((p) => (
          <Chip
            key={p.projectId}
            icon={<FolderOutlinedIcon sx={{ fontSize: '12px !important' }} />}
            label={p.projectName}
            size="small"
            variant="outlined"
            sx={{ fontSize: '11px', height: 22, maxWidth: 140 }}
          />
        ))}
      </Box>
    )}
  </Box>
);

const DeleteRoleModal = ({
  open, loading, usersLoading, roleToDelete, assignedData,
  selectedNewRoleId, onNewRoleChange, reassignableRoles,
  onClose, onConfirm, error, onClearError,
}) => {
  const normalisedData = useMemo(() => {
    if (!assignedData) return null;
    if (assignedData.scope === 'org') {
      return {
        scope: 'org',
        users: (assignedData.users || []).map((u) => ({
          id:             u.id             ?? u.user_id,
          name:           u.name           ?? u.user_name,
          email:          u.email          ?? u.user_email,
          avatarInitials: u.avatarInitials ?? u.avatar_initials,
        })),
      };
    }
    if (assignedData.scope === 'project') {
      return {
        scope: 'project',
        assignments: (assignedData.assignments || []).map((a) => ({
          userId:         a.user_id         ?? a.userId,
          userName:       a.user_name       ?? a.userName,
          userEmail:      a.user_email      ?? a.userEmail,
          avatarInitials: a.avatar_initials ?? a.avatarInitials,
          assignments:    (a.assignments || []).map((sub) => ({
            assignmentId: sub.assignment_id ?? sub.assignmentId,
            projectId:    sub.project_id    ?? sub.projectId,
            projectName:  sub.project_name  ?? sub.projectName,
          })),
        })),
      };
    }
    return assignedData;
  }, [assignedData]);

  const roleOptions = useMemo(
    () => reassignableRoles.map((r) => ({ id: r.id, name: r.name })),
    [reassignableRoles],
  );

  if (!roleToDelete) return null;

  const hasAssignees =
    normalisedData &&
    ((normalisedData.scope === 'org' && normalisedData.users?.length > 0) ||
      (normalisedData.scope === 'project' && normalisedData.assignments?.length > 0));

  const count = normalisedData?.scope === 'org'
    ? normalisedData.users?.length
    : normalisedData?.assignments?.length;

  const renderBody = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
      <Alert icon={<WarningAmberOutlinedIcon fontSize="small" />} severity="warning" sx={{ fontSize: '13px' }}>
        You are about to permanently delete <strong>{roleToDelete.name}</strong>. This action cannot be undone.
      </Alert>

      {error && (
        <Alert severity="error" onClose={onClearError} sx={{ py: 0.5, fontSize: '13px' }}>
          {error}
        </Alert>
      )}

      {usersLoading ? (
        <Loader />
      ) : !hasAssignees ? (
        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '13px' }}>
          No users are currently assigned this role. You can safely delete it.
        </Typography>
      ) : (
        <>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '13px' }}>
            This role is assigned to <strong>{count}</strong> user{count > 1 ? 's' : ''}.
            Select a replacement — all listed users will be moved to it on <strong>Save &amp; Delete</strong>.
          </Typography>

          <CustomSelect
            id="reassign-role"
            label="Reassign all users to"
            placeholder="Select new role"
            value={selectedNewRoleId || ''}
            options={roleOptions}
            onChange={onNewRoleChange}
          />

          {/* Scrollable card list — only this part scrolls */}
          <Box sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1.5,
            maxHeight: 220,
            overflowY: 'auto',
            overflowX: 'hidden',
            bgcolor: 'background.paper',
          }}>
            {normalisedData.scope === 'org'
              ? normalisedData.users.map((u) => (
                <UserCard key={u.id} initials={u.avatarInitials} name={u.name} email={u.email} />
              ))
              : normalisedData.assignments.map((a) => (
                <UserCard
                  key={a.userId}
                  initials={a.avatarInitials}
                  name={a.userName}
                  email={a.userEmail}
                  projects={a.assignments}
                />
              ))
            }
          </Box>
        </>
      )}
    </Box>
  );

  return (
    <ConfirmationModal
      show={open}
      handleClose={onClose}
      handleConfirm={onConfirm}
      title="Delete role"
      buttonLabel={hasAssignees ? 'Save & Delete' : 'Delete role'}
      buttonStyle="error"
      loading={loading || usersLoading}
      hideConfirmButton={false}
      body={renderBody()}
    />
  );
};

export default DeleteRoleModal;